import React, { useState } from 'react';

import { connect } from 'react-redux';

import { userSelector } from '../../reducers/current-user-selectors';

import { getAgentForActivity } from '../../contact-activity/components/contact-activity-utils';
import { UiButton } from '../../ui/ui-button';

import { CommentContactActivityDetails } from './comment-contact-activity-details';
import { CommentEditor } from './comment-editor';

import { formatDateTime } from '../../util/formatters';
import { confirm } from '../../widgets/confirmation-dialog';
import Message from '../../widgets/legacy-message';

const mapStateToProps = (state: any) => ({
  user: userSelector(state),
});

type InputProps = {
  idKey: number;
  comments: Comment[];
  saveComment: (comment: string) => void;
  editComment: (commentId: number, comment: string) => void;
  saveDraft: (draft: string, commentId?: number) => void;
  clearDraft: () => void;
  deleteComment: (commentId: number) => void;
  profileType: ContactType;
  commentDraft?: AutosavedDraft;
};

type ReduxProps = ReturnType<typeof mapStateToProps>;
type Props = InputProps & ReduxProps;

export const CommentList = connect(mapStateToProps)(
  ({
    user,
    comments,
    saveComment,
    editComment,
    deleteComment,
    profileType,
    saveDraft,
    clearDraft,
    idKey,
    commentDraft,
  }: Props) => {
    const [editingCommentId, setEditingCommentId] = useState<number | null>(
      // If the draft comment has a commentId, that means it is a draft editing an existing comment
      commentDraft?.commentId || null,
    );

    const handleDelete = (commentId: number) => async () => {
      if (await confirm('Are you sure you want to delete this note?')) {
        deleteComment(commentId);
      }
    };

    const toggleEditing = (commentId: number) => async () => {
      let confirmEdit = true;
      if (commentDraft?.messageBody) {
        confirmEdit = Boolean(
          await confirm(
            'You will lose your current draft if you switch to editing, are you sure you want to continue without saving your comment?',
          ),
        );
      }
      if (!confirmEdit) {
        return;
      }
      const commentToEdit = comments.find((c) => c.commentId === commentId);
      saveDraft(commentToEdit?.comment || '', commentToEdit?.commentId);
      setEditingCommentId(commentId);
    };

    const updateComment = (commentId: number) => (comment: string) => {
      editComment(commentId, comment);
      setEditingCommentId(null);
    };

    const cancelEditComment = () => {
      clearDraft();
      setEditingCommentId(null);
    };

    const DraftDate = ({ draft }: { draft: AutosavedDraft | undefined }) =>
      draft?.messageBody ? (
        <div className="typo-subtitle u-textAlign-right u-width-100">
          Draft saved {draft?.savedAt.format('dddd, MMMM Do, h:mm a')}
        </div>
      ) : null;

    return (
      <div>
        {comments?.map((c) => {
          const { activity } = c;
          const agentName = getAgentForActivity(activity);
          const datetime = formatDateTime(activity.datetime);

          return (
            <Message className="u-margin-y" canBeDismissed={false} key={c.commentId}>
              <div>
                <div className="typo-subtitle">
                  {agentName} on {datetime}
                </div>
                <CommentContactActivityDetails contactActivity={activity} />
                {editingCommentId === c.commentId ? (
                  <>
                    <DraftDate draft={commentDraft} />
                    <CommentEditor
                      key={`editing-${idKey}`}
                      comment={commentDraft?.messageBody || c.comment}
                      saveComment={updateComment(c.commentId)}
                      saveDraft={saveDraft}
                      clearDraft={clearDraft}
                      cancel={cancelEditComment}
                      commentId={editingCommentId}
                      isEditing
                    />
                  </>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: c.comment }} />
                )}
              </div>
              {activity.username === user.username && editingCommentId !== c.commentId && (
                <div className="u-display-flex u-justifyContent-flexEnd">
                  <div className="u-margin-right-md">
                    <UiButton
                      onClick={toggleEditing(c.commentId)}
                      variant="tertiary"
                      size="xs"
                      className="u-alignSelf-flexEnd"
                      disabled={!!editingCommentId}
                    >
                      Edit
                    </UiButton>
                  </div>
                  <div>
                    <UiButton
                      onClick={handleDelete(c.commentId)}
                      variant="tertiary"
                      size="xs"
                      className="u-alignSelf-flexEnd"
                      disabled={!!editingCommentId}
                    >
                      Delete
                    </UiButton>
                  </div>
                </div>
              )}
            </Message>
          );
        })}
        <div className="u-margin-top-lg u-margin-bottom-sm">
          Notes are not shown to the {profileType}.
        </div>
        {editingCommentId ? null : (
          <>
            <DraftDate draft={commentDraft} />
            <CommentEditor
              key={idKey}
              // check for commentId in draft to avoid flashing the wrong draft in the text box before it is cleared from redux
              // here we only want drafts for _new_ comments
              comment={commentDraft?.commentId ? '' : commentDraft?.messageBody || ''}
              saveComment={saveComment}
              saveDraft={saveDraft}
              clearDraft={clearDraft}
            />
          </>
        )}
      </div>
    );
  },
);
